import {
  formatSpeakerDisplayLabel,
  formatVoiceLabel,
} from '@app/modules/diarization/speaker-utils';
import { PersonLane } from '@app/modules/persons/appearances/types';
import { usePerson } from '@app/modules/persons/hooks/usePerson';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { personsTrackMessages } from '../shared/persons-track-messages';

export type PersonLaneIdentity = {
  /** Resolved person DTO when available. */
  person: ReturnType<typeof usePerson>;
  /** Display name as ReactNode (FormattedMessage for unknowns, string otherwise). */
  nameNode: ReactNode;
  /** Plain-string name for aria / avatar fallback. */
  nameLabel: string;
  /** Profession when known; undefined otherwise. */
  profession: string | undefined;
  /** Explicit avatar src (crop for unknown faces). */
  avatarSrc: string | undefined;
  /** Fallback used when person is still loading. */
  fallbackName: string;
  /**
   * False while a face-only lane is waiting on Persons batch (no name yet).
   * Callers must not render bars/avatars until this is true.
   */
  isIdentityReady: boolean;
};

/**
 * Shared identity resolution for a person lane (row label, popover, table cell).
 *
 * Face payloads have no name — only `person_id`. While the Persons batch is still
 * warming the cache (`person === undefined`), keep the fallback (often empty for
 * face-only) and do **not** flash the unknown label. Use unknown only when the
 * lane is flagged unknown, batch confirmed the id missing (`person === null`),
 * or the DTO has no usable name and there is no voice fallback.
 */
export function usePersonLaneIdentity(lane: PersonLane): PersonLaneIdentity {
  const person = usePerson(lane.personId);
  const { formatMessage } = useIntl();
  const fallbackName = formatSpeakerDisplayLabel(formatVoiceLabel(lane.fallbackLabel) || '');
  const unknownLabel = formatMessage(personsTrackMessages.unknown);

  const personName = person?.name?.trim() || '';
  const resolvedName = personName || fallbackName.trim();
  // undefined = still loading; null = batch confirmed missing.
  const lookupFailed = person === null;
  const dtoWithoutName = person != null && !resolvedName;
  const showAsUnknown = lane.isUnknown || lookupFailed || dtoWithoutName;
  // Hide UI until we have a real label — never paint empty "— Rostro —" mappings.
  const isIdentityReady = showAsUnknown || !!resolvedName;

  const nameLabel = showAsUnknown ? unknownLabel : resolvedName;
  const nameNode = nameLabel;
  const rawProfession = showAsUnknown ? undefined : person?.profession?.trim() || undefined;
  const profession = rawProfession
    ? rawProfession.charAt(0).toUpperCase() + rawProfession.slice(1)
    : undefined;

  return {
    person,
    nameNode,
    nameLabel,
    profession,
    avatarSrc: showAsUnknown ? lane.crop : undefined,
    fallbackName: showAsUnknown ? unknownLabel : fallbackName || unknownLabel,
    isIdentityReady,
  };
}
